import { useState } from 'react';
import { api, fmtDate, sinceLabel } from '../api';
import { useAuth } from '../AuthContext';
import {
  IconBank,
  IconChat,
  IconCheck,
  IconCopy,
  IconDatabase,
  IconDownload,
  IconGift,
  IconSettings,
  IconUsers,
  IconX,
} from '../components/Icons';
import { downloadExcel, excelSheet } from '../utils/excelExport';
import WhatsAppSettings from '../components/WhatsAppSettings';
import BaseImport from '../components/BaseImport';

function resizeAvatar(file) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) return reject(new Error('Selecciona una imagen JPG, PNG o WebP'));
    if (file.size > 8 * 1024 * 1024) return reject(new Error('La imagen original no puede superar 8 MB'));

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer la imagen'));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error('La imagen no es válida'));
      image.onload = () => {
        const size = 320;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        const sourceSize = Math.min(image.width, image.height);
        const sx = (image.width - sourceSize) / 2;
        const sy = (image.height - sourceSize) / 2;
        ctx.drawImage(image, sx, sy, sourceSize, sourceSize, 0, 0, size, size);
        let result = canvas.toDataURL('image/webp', 0.82);
        if (!result.startsWith('data:image/webp')) result = canvas.toDataURL('image/jpeg', 0.82);
        resolve(result);
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

const dateForFile = () => new Date().toISOString().slice(0, 10);
const textDate = (date) => (date ? fmtDate(date) : 'Sin fecha');
// Las hojas que se pueden volver a importar llevan la fecha en formato
// ordenable y sin ambigüedad de día/mes.
const isoDate = (date) => (date ? new Date(date).toISOString().slice(0, 10) : '');
const serviceName = (subscription) => (
  subscription.kind === 'combo' ? subscription.comboId?.name : subscription.platformId?.name
) || 'Sin servicio';

export default function Settings() {
  const { user, setUser } = useAuth();
  const [section, setSection] = useState('profile');
  const [tool, setTool] = useState('messages');
  const [form, setForm] = useState({
    name: user.name || '',
    businessName: user.businessName || '',
    phone: user.phone || '',
    avatar: user.avatar || '',
  });
  const [profileState, setProfileState] = useState({ message: '', ok: false });
  const [exportState, setExportState] = useState({ busy: '', message: '', ok: false });
  const [baseOpen, setBaseOpen] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [referralCopied, setReferralCopied] = useState(false);
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState('');
  const [pwOk, setPwOk] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const setP = (key) => (e) => setPw({ ...pw, [key]: e.target.value });
  const initials = (form.businessName || form.name || 'SC').slice(0, 2).toUpperCase();

  const pickAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAvatarBusy(true);
    setProfileState({ message: '', ok: false });
    try {
      const avatar = await resizeAvatar(file);
      setForm((current) => ({ ...current, avatar }));
      setProfileState({ message: 'Foto lista. Guarda los cambios para aplicarla.', ok: true });
    } catch (err) {
      setProfileState({ message: err.message, ok: false });
    } finally {
      setAvatarBusy(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileState({ message: '', ok: false });
    try {
      const d = await api('/auth/me', {
        method: 'PUT',
        body: {
          name: form.name,
          businessName: form.businessName,
          phone: form.phone,
          avatar: form.avatar,
        },
      });
      setUser(d.user);
      setProfileState({ message: 'Los datos del perfil se guardaron correctamente.', ok: true });
    } catch (err) {
      setProfileState({ message: err.message, ok: false });
    }
  };

  const changePass = async (e) => {
    e.preventDefault();
    setPwMsg('');
    if (pw.newPassword.length < 6) {
      setPwOk(false);
      setPwMsg('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (pw.newPassword !== pw.confirm) {
      setPwOk(false);
      setPwMsg('Las contraseñas nuevas no coinciden');
      return;
    }
    try {
      await api('/auth/password', { method: 'PUT', body: { currentPassword: pw.currentPassword, newPassword: pw.newPassword } });
      setPwOk(true);
      setPwMsg('Contraseña actualizada');
      setPw({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) {
      setPwOk(false);
      setPwMsg(err.message);
    }
  };

  const copyReferral = async () => {
    if (!user.referralCode) return;
    try {
      await navigator.clipboard.writeText(user.referralCode);
      setReferralCopied(true);
      window.setTimeout(() => setReferralCopied(false), 2200);
    } catch {
      setProfileState({ message: 'No se pudo copiar el código. Puedes seleccionarlo manualmente.', ok: false });
    }
  };

  const exportOperation = async () => {
    setExportState({ busy: 'operation', message: '', ok: false });
    try {
      const [accounts, subscriptions, clients, platforms, combos] = await Promise.all([
        api('/accounts'),
        api('/subscriptions'),
        api('/clients'),
        api('/platforms'),
        api('/combos'),
      ]);

      const summary = [
        ['Negocio', user.businessName || user.name],
        ['Fecha del respaldo', new Date().toLocaleString('es-CR')],
        ['Cuentas del banco', accounts.length],
        ['Perfiles totales', accounts.reduce((sum, account) => sum + Number(account.totalProfiles || 0), 0)],
        ['Perfiles ocupados', accounts.reduce((sum, account) => sum + Number(account.usedProfiles || 0), 0)],
        ['Suscripciones', subscriptions.length],
        ['Clientes', clients.length],
        ['Plataformas', platforms.length],
        ['Combos', combos.length],
      ];

      const accountRows = accounts.map((account) => [
        account.platformId?.name || '',
        account.kind === 'perfiles' ? 'Perfiles sueltos' : 'Cuenta completa',
        account.email || '',
        account.password || '',
        Number(account.totalProfiles || 0),
        account.renewalDay || '',
        Number(account.usedProfiles || 0),
        Number(account.availableProfiles || 0),
        account.kind === 'perfiles' ? 'Perfiles sueltos' : 'Cuenta completa',
        account.active === false ? 'Inactiva' : 'Activa',
        Number(account.cost || 0),
        account.notes || '',
        textDate(account.createdAt),
      ]);

      const countryCode = user.whatsapp?.countryCode || '506';
      const subscriptionRows = subscriptions.map((subscription) => [
        serviceName(subscription),
        subscription.clientId?.name || '',
        subscription.clientId?.phone || '',
        countryCode,
        subscription.accountEmail || '',
        subscription.accountPassword || '',
        subscription.profileName || '',
        subscription.pin || '',
        Number(subscription.price || 0),
        isoDate(subscription.startDate),
        isoDate(subscription.nextDueDate),
        subscription.unit === 'cuenta' ? 'Cuenta completa' : 'Perfil',
        subscription.status || '',
      ]);

      const clientRows = clients.map((client) => [
        client.name || '',
        user.whatsapp?.countryCode || '506',
        client.phone || '',
        client.email || '',
        Number(client.activeSubs || 0),
        client.notes || '',
        textDate(client.createdAt),
      ]);

      const catalogRows = [
        ...platforms.map((platform) => [
          'Plataforma', platform.name, platform.profileEnabled ? 'Sí' : 'No', Number(platform.profilePrice || 0), Number(platform.profileCost || 0),
          platform.accountEnabled ? 'Sí' : 'No', Number(platform.accountPrice || 0), Number(platform.accountCost || 0), platform.notes || '',
        ]),
        ...combos.map((combo) => [
          'Combo', combo.name, combo.profileEnabled ? 'Sí' : 'No', Number(combo.profilePrice || 0), Number(combo.profileCost || 0),
          combo.accountEnabled ? 'Sí' : 'No', Number(combo.accountPrice || 0), Number(combo.accountCost || 0),
          (combo.platformIds || []).map((platform) => platform.name || platform).join(' + '),
        ]),
      ];

      await downloadExcel(`StreamControl_respaldo_${dateForFile()}.xlsx`, [
        excelSheet('Resumen', [{ label: 'Dato', width: 28 }, { label: 'Valor', width: 34 }], summary),
        excelSheet('Cuentas', [
          { label: 'Plataforma', width: 20 }, { label: 'Correo', width: 30 }, { label: 'Contraseña', width: 22 },
          { label: 'Perfiles totales', width: 15 }, { label: 'Renovación cuenta', width: 17 },
          { label: 'Perfiles usados', width: 15 }, { label: 'Disponibles', width: 13 }, { label: 'Tipo', width: 18 },
          { label: 'Estado', width: 12 }, { label: 'Costo mensual', width: 16 }, { label: 'Notas', width: 30 },
          { label: 'Creada', width: 18 },
        ], accountRows),
        excelSheet('Suscripciones', [
          { label: 'Plataforma', width: 20 }, { label: 'Cliente', width: 24 }, { label: 'WhatsApp', width: 18 },
          { label: 'Cod. país', width: 10 }, { label: 'Correo cuenta', width: 30 }, { label: 'Contraseña', width: 22 },
          { label: 'Perfil', width: 18 }, { label: 'Perfil / PIN', width: 12 }, { label: 'Precio', width: 14 },
          { label: 'Fecha inicio', width: 14 }, { label: 'Fecha vencimiento', width: 16 },
          { label: 'Modalidad', width: 16 }, { label: 'Estado', width: 14 },
        ], subscriptionRows),
        excelSheet('Clientes', [
          { label: 'Nombre', width: 24 }, { label: 'Cod. país', width: 10 }, { label: 'WhatsApp', width: 18 }, { label: 'Correo', width: 30 },
          { label: 'Suscripciones activas', width: 20 }, { label: 'Notas', width: 30 }, { label: 'Cliente desde', width: 18 },
        ], clientRows),
        excelSheet('Catálogo', [
          { label: 'Tipo', width: 14 }, { label: 'Nombre', width: 24 }, { label: 'Vende perfiles', width: 15 },
          { label: 'Precio perfil', width: 15 }, { label: 'Costo perfil', width: 15 }, { label: 'Vende cuentas', width: 15 },
          { label: 'Precio cuenta', width: 15 }, { label: 'Costo cuenta', width: 15 }, { label: 'Notas / plataformas', width: 34 },
        ], catalogRows),
      ]);
      setExportState({ busy: '', message: 'Respaldo operativo descargado correctamente.', ok: true });
    } catch (err) {
      setExportState({ busy: '', message: err.message || 'No se pudo generar el archivo.', ok: false });
    }
  };

  const exportTenant = async () => {
    setExportState({ busy: 'tenant', message: '', ok: false });
    try {
      const rows = [
        ['Nombre del responsable', user.name || ''],
        ['Nombre del negocio', user.businessName || ''],
        ['Correo de acceso', user.email || ''],
        ['WhatsApp', user.phone || ''],
        ['Tipo de perfil', 'Tenant · vendedor'],
        ['Plan', user.plan === 'anual' ? 'Anual' : 'Mensual'],
        ['Estado', user.blocked ? 'Acceso bloqueado' : 'Activo'],
        ['Vencimiento del plan', textDate(user.planExpires)],
        ['Fecha de creación', textDate(user.createdAt)],
        ['Antigüedad', user.createdAt ? sinceLabel(user.createdAt) : 'No disponible'],
        ['Código de referido', user.referralCode || ''],
        ['Foto de perfil', user.avatar ? 'Configurada' : 'Sin configurar'],
        ['Moneda', user.currency || 'CRC'],
        ['Contraseña de acceso', 'Protegida: no es recuperable ni exportable'],
      ];
      await downloadExcel(`StreamControl_datos_tenant_${dateForFile()}.xlsx`, [
        excelSheet('Perfil del tenant', [{ label: 'Campo', width: 30 }, { label: 'Información', width: 48 }], rows),
      ]);
      setExportState({ busy: '', message: 'Datos del tenant descargados correctamente.', ok: true });
    } catch (err) {
      setExportState({ busy: '', message: err.message || 'No se pudo generar el archivo.', ok: false });
    }
  };

  return (
    <div className="settings-page">
      <header className="page-hero settings-hero">
        <div>
          <div className="eyebrow">Configuración</div>
          <h1>Ajustes del negocio</h1>
          <p>Administra tu identidad, seguridad y herramientas operativas.</p>
        </div>
      </header>

      <nav className="settings-primary-tabs" role="tablist" aria-label="Secciones de ajustes">
        <button type="button" role="tab" aria-selected={section === 'profile'} className={section === 'profile' ? 'is-active' : ''} onClick={() => setSection('profile')}>
          <IconUsers size={17} /><span><strong>Perfil</strong><small>Identidad y seguridad</small></span>
        </button>
        <button type="button" role="tab" aria-selected={section === 'tools'} className={section === 'tools' ? 'is-active' : ''} onClick={() => setSection('tools')}>
          <IconSettings size={17} /><span><strong>Herramientas</strong><small>Mensajes y respaldos</small></span>
        </button>
      </nav>

      {section === 'profile' && (
        <div className="settings-layout">
          <form onSubmit={saveProfile} className="settings-main-column">
            <section className="settings-section">
              <div className="settings-section-head">
                <span><IconUsers size={18} /></span>
                <div><h2>Perfil del negocio</h2><p>Datos que identifican tu espacio de trabajo.</p></div>
              </div>
              <div className="settings-avatar-row">
                <span className={`settings-avatar ${form.avatar ? 'has-image' : ''}`}>
                  {form.avatar ? <img src={form.avatar} alt="Vista previa de tu foto" /> : initials}
                </span>
                <div className="settings-avatar-copy">
                  <strong>Foto de perfil</strong>
                  <p>Se recorta automáticamente en formato cuadrado. Máximo 8 MB.</p>
                  <div className="settings-avatar-actions">
                    <label className="btn-ghost">
                      <input type="file" accept="image/png,image/jpeg,image/webp" onChange={pickAvatar} className="sr-only" />
                      {avatarBusy ? 'Procesando…' : form.avatar ? 'Cambiar foto' : 'Subir foto'}
                    </label>
                    {form.avatar && <button type="button" onClick={() => setForm({ ...form, avatar: '' })}><IconX size={14} /> Quitar</button>}
                  </div>
                </div>
              </div>
              <div className="settings-fields-grid">
                <label className="field-label">Tu nombre
                  <input className="input mt-2" value={form.name} onChange={set('name')} required />
                </label>
                <label className="field-label">Nombre del negocio
                  <input className="input mt-2" value={form.businessName} onChange={set('businessName')} />
                </label>
                <label className="field-label form-span">WhatsApp del negocio
                  <input className="input mt-2" value={form.phone} onChange={set('phone')} placeholder="8888 1234" />
                </label>
              </div>
            </section>
            {profileState.message && <p className={profileState.ok ? 'settings-success' : 'form-error'}>{profileState.ok && <IconCheck size={15} />} {profileState.message}</p>}
            <button className="btn-primary settings-save">Guardar perfil</button>
          </form>

          <aside className="settings-side-column">
            <section className="settings-section referral-card">
              <div className="settings-section-head">
                <span><IconGift size={18} /></span>
                <div><h2>Programa de referidos</h2><p>Comparte tu código con otro vendedor.</p></div>
              </div>
              <div className="referral-code-box">
                <div><span>Tu código</span><strong>{user.referralCode || '---000'}</strong></div>
                <button type="button" onClick={copyReferral} disabled={!user.referralCode} aria-label="Copiar código de referido">
                  {referralCopied ? <IconCheck size={17} /> : <IconCopy size={17} />}{referralCopied ? 'Copiado' : 'Copiar'}
                </button>
              </div>
              <p className="referral-note">Tiene 3 letras y 3 números. El nuevo tenant recibirá un mes gratis al usarlo.</p>
            </section>

            <form onSubmit={changePass} className="settings-section settings-password-card">
              <div className="settings-section-head">
                <span><IconSettings size={18} /></span>
                <div><h2>Seguridad</h2><p>Actualiza tu contraseña de acceso.</p></div>
              </div>
              <label className="field-label">Contraseña actual
                <input className="input mt-2" type="password" value={pw.currentPassword} onChange={setP('currentPassword')} required />
              </label>
              <label className="field-label">Nueva contraseña
                <input className="input mt-2" type="password" value={pw.newPassword} onChange={setP('newPassword')} placeholder="Mínimo 6 caracteres" required />
              </label>
              <label className="field-label">Repetir contraseña
                <input className="input mt-2" type="password" value={pw.confirm} onChange={setP('confirm')} required />
              </label>
              {pwMsg && <p className={pwOk ? 'settings-success' : 'form-error'}>{pwOk && <IconCheck size={15} />} {pwMsg}</p>}
              <button className="btn-ghost w-full">Cambiar contraseña</button>
            </form>
          </aside>
        </div>
      )}

      {section === 'tools' && (
        <div className="settings-tools-layout">
          <aside className="settings-tools-nav" aria-label="Herramientas disponibles">
            <div className="eyebrow">Herramientas</div>
            <button type="button" className={tool === 'messages' ? 'is-active' : ''} onClick={() => setTool('messages')}>
              <IconChat size={17} /><span><strong>Mensajes automáticos</strong><small>Plantillas de WhatsApp</small></span>
            </button>
            <button type="button" className={tool === 'data' ? 'is-active' : ''} onClick={() => setTool('data')}>
              <IconDatabase size={17} /><span><strong>Datos y cuentas</strong><small>Exportaciones y respaldo</small></span>
            </button>
          </aside>

          <div className="settings-tools-content">
            {tool === 'messages' && <WhatsAppSettings />}

            {tool === 'data' && (
              <section className="settings-section settings-data-tool">
                <div className="settings-section-head">
                  <span><IconDatabase size={18} /></span>
                  <div><h2>Datos y cuentas</h2><p>Genera respaldos organizados que puedes abrir directamente en Excel.</p></div>
                </div>
                <div className="export-grid">
                  <article className="export-card">
                    <span className="export-card-icon"><IconBank size={21} /></span>
                    <div className="export-card-copy">
                      <span className="export-kicker">Respaldo operativo</span>
                      <h3>Cuentas, perfiles y catálogo</h3>
                      <p>Incluye cuentas del banco, credenciales, perfiles asignados, clientes, suscripciones y precios.</p>
                      <div className="export-sheet-list"><span>5 hojas</span><span>Formato .xlsx</span><span>Datos completos</span></div>
                    </div>
                    <button type="button" className="btn-primary" onClick={exportOperation} disabled={!!exportState.busy}>
                      <IconDownload size={16} /> {exportState.busy === 'operation' ? 'Preparando…' : 'Exportar operación'}
                    </button>
                  </article>

                  <article className="export-card">
                    <span className="export-card-icon"><IconDatabase size={21} /></span>
                    <div className="export-card-copy">
                      <span className="export-kicker">Cargar desde Excel</span>
                      <h3>Base de datos completa</h3>
                      <p>Sube el archivo con las hojas Suscripciones, Cuentas y Clientes y se crean solos.</p>
                      <div className="export-sheet-list"><span>3 hojas</span><span>Formato .xlsx</span><span>No duplica</span></div>
                    </div>
                    <button type="button" className="btn-primary" onClick={() => setBaseOpen(true)} disabled={!!exportState.busy}>
                      <IconDatabase size={16} /> Exportar base de datos
                    </button>
                  </article>

                  <article className="export-card">
                    <span className="export-card-icon is-mint"><IconUsers size={21} /></span>
                    <div className="export-card-copy">
                      <span className="export-kicker">Identidad del tenant</span>
                      <h3>Perfil y antigüedad</h3>
                      <p>Exporta responsable, negocio, contacto, plan, vencimiento, antigüedad y código de referido.</p>
                      <div className="export-sheet-list"><span>1 hoja</span><span>Formato .xlsx</span><span>Sin contraseña</span></div>
                    </div>
                    <button type="button" className="btn-ghost" onClick={exportTenant} disabled={!!exportState.busy}>
                      <IconDownload size={16} /> {exportState.busy === 'tenant' ? 'Preparando…' : 'Exportar datos del tenant'}
                    </button>
                  </article>
                </div>
                <div className="export-security-note">
                  <IconSettings size={17} />
                  <div><strong>Resguarda el archivo operativo.</strong><p>Contiene correos, contraseñas y PIN de las cuentas de streaming. La contraseña de acceso a StreamControl nunca se exporta porque está protegida mediante cifrado irreversible.</p></div>
                </div>
                {exportState.message && <p className={exportState.ok ? 'settings-success' : 'form-error'}>{exportState.ok && <IconCheck size={15} />} {exportState.message}</p>}
              </section>
            )}
          </div>
        </div>
      )}

      <BaseImport
        open={baseOpen}
        onClose={() => setBaseOpen(false)}
        onDone={() => setExportState({ busy: '', message: 'Base cargada. Revísala en Clientes y Suscripciones.', ok: true })}
      />
    </div>
  );
}
